import React, { useState } from 'react';
import {
  Paper, Box, TextField, Button, Typography, Grid,
  FormControl, InputLabel, Select, MenuItem, Divider,
  FormControlLabel, Checkbox, Collapse
} from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';

const ConsoleInputForm = ({ onAnalyze, onInputChange, initialData, loading }) => {
  const [formData, setFormData] = useState(initialData || {
    console_name: 'Console Analysis',
    // Column properties
    console_column_width: 0.3,
    console_column_depth: 0.3,
    console_column_height: 1.0,
    // Console geometry
    console_height: 0.3,
    // Reinforcement
    console_main_rebar_diameter: 0.016,
    console_main_rebar_count: 4,
    console_shear_rebar_diameter: 0.008,
    console_shear_rebar_spacing: 0.15,
    // Loads
    console_vertical_load: 500,
    console_horizontal_load: 50,
    console_torsion_moment: 0.0,
    // Materials
    console_concrete_grade: 'C30/37',
    console_steel_grade: 'B500B',
    console_exposure_class: 'XC1',
    console_tolerance: 0.01,
    // Bearing pad
    console_neoprene_pad_enabled: true,
    console_shore_hardness: 70,
    console_neoprene_temperature: 20,
    // Bearing pad manual override (auto-optimized by default)
    console_bearing_pad_manual_override: false,
    console_bearing_pad_depth: 0.15,
    console_neoprene_layers: 2,
    console_neoprene_thickness: 0.012,
    console_steel_plate_thickness: 0.003,
    // Beam details
    console_beam_type: 'steel',
    console_beam_width: 0.2,
    console_beam_depth: 0.5,
    console_beam_angular_change: 0.0,
    // Direct bearing (steel): optional I [m⁴]; omit for rigid default
    console_beam_second_moment_of_area: null,
    // Dowel details
    console_dowel_material: 'steel',
    console_dowel_type: 'rebar',
    console_dowel_diameter: 0.016,
    console_dowel_count: 4
  });

  const handleChange = (field) => (eventOrValue) => {
    let value;
    if (eventOrValue && eventOrValue.target) {
      const { type, value: rawValue } = eventOrValue.target;
      value = type === 'number' ? parseFloat(rawValue) || 0 : rawValue;
    } else {
      value = typeof eventOrValue === 'number' ? eventOrValue : eventOrValue ?? '';
    }
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      if (onInputChange) {
        onInputChange(newData);
      }
      return newData;
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = { ...formData };
    const I = payload.console_beam_second_moment_of_area;
    if (I === '' || I === null || I === undefined || Number.isNaN(Number(I))) {
      delete payload.console_beam_second_moment_of_area;
    } else {
      payload.console_beam_second_moment_of_area = Number(I);
    }
    onAnalyze(payload);
  };

  return (
    <Paper elevation={3} sx={{
      p: 2,
      '& .MuiInputBase-root': {
        fontSize: '0.875rem', // Slightly smaller font
      },
      '& .MuiFormHelperText-root': {
        margin: 0,
        marginTop: '2px',
        fontSize: '0.7rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <EngineeringIcon sx={{ mr: 1 }} />
        <Typography variant="h5" component="h2">
          Console Analysis Input
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Project Info */}
          <Grid item xs={12}>
            <Typography variant="h6">
              Project Information
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              label="Analysis Name"
              value={formData.console_name}
              onChange={handleChange('console_name')}
              variant="outlined"
            />
          </Grid>

          {/* Column Properties */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Column Properties
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Column Width (m)"
              value={formData.console_column_width}
              onChange={handleChange('console_column_width')}
              inputProps={{ min: 0.1, max: 2, step: 0.01 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Column Depth (m)"
              value={formData.console_column_depth}
              onChange={handleChange('console_column_depth')}
              inputProps={{ min: 0.1, max: 2, step: 0.01 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Column Height (m)"
              value={formData.console_column_height}
              onChange={handleChange('console_column_height')}
              inputProps={{ min: 0.2, max: 10, step: 0.1 }}
              variant="outlined"
              helperText="Available for anchorage"
            />
          </Grid>

          {/* Console Geometry */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Console Geometry
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Console Height (m)"
              value={formData.console_height}
              onChange={handleChange('console_height')}
              inputProps={{ min: 0.05, max: 2, step: 0.01 }}
              variant="outlined"
              helperText="Distance console extends downwards"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Tolerance (mm)"
              value={formData.console_tolerance * 1000}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0;
                handleChange('console_tolerance')({ target: { value: value / 1000 } });
              }}
              inputProps={{ min: 0, max: 50, step: 1 }}
              variant="outlined"
              helperText="Construction tolerance"
            />
          </Grid>

          {/* Reinforcement */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Column Reinforcement (Inputs)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Console reinforcement is calculated by the app; inputs apply to column check only.
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Main Rebar Diameter (mm)"
              value={formData.console_main_rebar_diameter * 1000}
              onChange={(e) => handleChange('console_main_rebar_diameter')((parseFloat(e.target.value) || 0) / 1000)}
              inputProps={{ min: 6, max: 32, step: 2 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Main Rebar Count"
              value={formData.console_main_rebar_count}
              onChange={handleChange('console_main_rebar_count')}
              inputProps={{ min: 1, max: 20 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Stirrup Diameter (mm)</InputLabel>
              <Select
                value={(formData.console_shear_rebar_diameter * 1000).toFixed(0)}
                onChange={(e) => handleChange('console_shear_rebar_diameter')(parseFloat(e.target.value) / 1000)}
                label="Stirrup Diameter (mm)"
              >
                <MenuItem value="6">6</MenuItem>
                <MenuItem value="8">8</MenuItem>
                <MenuItem value="10">10</MenuItem>
                <MenuItem value="12">12</MenuItem>
                <MenuItem value="16">16</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Stirrup Spacing (mm)"
              value={formData.console_shear_rebar_spacing * 1000}
              onChange={(e) => handleChange('console_shear_rebar_spacing')((parseFloat(e.target.value) || 0) / 1000)}
              inputProps={{ min: 50, max: 300, step: 25 }}
              variant="outlined"
            />
          </Grid>

          {/* Loads */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Loads
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Vertical Load (kN)"
              value={formData.console_vertical_load}
              onChange={handleChange('console_vertical_load')}
              inputProps={{ min: 0, max: 2000, step: 10 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Horizontal Load (kN)"
              value={formData.console_horizontal_load}
              onChange={handleChange('console_horizontal_load')}
              inputProps={{ min: 0, max: 500, step: 5 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Torsion Moment (kNm)"
              value={formData.console_torsion_moment}
              onChange={handleChange('console_torsion_moment')}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              variant="outlined"
              helperText="From eccentric loading"
            />
          </Grid>

          {/* Materials */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Materials
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Concrete Grade</InputLabel>
              <Select
                value={formData.console_concrete_grade}
                onChange={handleChange('console_concrete_grade')}
                label="Concrete Grade"
              >
                <MenuItem value="C20/25">C20/25</MenuItem>
                <MenuItem value="C25/30">C25/30</MenuItem>
                <MenuItem value="C30/37">C30/37</MenuItem>
                <MenuItem value="C35/45">C35/45</MenuItem>
                <MenuItem value="C40/50">C40/50</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Steel Grade</InputLabel>
              <Select
                value={formData.console_steel_grade}
                onChange={handleChange('console_steel_grade')}
                label="Steel Grade"
              >
                <MenuItem value="B500B">B500B</MenuItem>
                <MenuItem value="B500C">B500C</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Exposure Class</InputLabel>
              <Select
                value={formData.console_exposure_class}
                onChange={handleChange('console_exposure_class')}
                label="Exposure Class"
              >
                <MenuItem value="XC1">XC1 - Dry environment</MenuItem>
                <MenuItem value="XC2">XC2 - Wet, rarely dry</MenuItem>
                <MenuItem value="XC3">XC3 - Moderate humidity</MenuItem>
                <MenuItem value="XC4">XC4 - Cyclic wet and dry</MenuItem>
                <MenuItem value="XD1">XD1 - Moderate chemical</MenuItem>
                <MenuItem value="XD2">XD2 - High chemical</MenuItem>
                <MenuItem value="XD3">XD3 - Very high chemical</MenuItem>
                <MenuItem value="XS1">XS1 - Air-borne salt</MenuItem>
                <MenuItem value="XS2">XS2 - Submerged</MenuItem>
                <MenuItem value="XS3">XS3 - Tidal/splash zone</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Bearing Pad Options */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Bearing Pad (EN 1337-3)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Pad width = column width. Depth is auto-optimized to minimize console projection unless manually overridden.
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Neoprene Pad</InputLabel>
              <Select
                value={formData.console_neoprene_pad_enabled}
                onChange={handleChange('console_neoprene_pad_enabled')}
                label="Neoprene Pad"
              >
                <MenuItem value={true}>Enabled</MenuItem>
                <MenuItem value={false}>Disabled</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Shore Hardness"
              value={formData.console_shore_hardness}
              onChange={handleChange('console_shore_hardness')}
              inputProps={{ min: 50, max: 70, step: 5 }}
              variant="outlined"
              disabled={!formData.console_neoprene_pad_enabled}
              helperText="EN 1337-3: 50-70 Shore A"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Design Temp (°C)"
              value={formData.console_neoprene_temperature}
              onChange={handleChange('console_neoprene_temperature')}
              inputProps={{ min: -30, max: 60, step: 1 }}
              variant="outlined"
              disabled={!formData.console_neoprene_pad_enabled}
              helperText="Affects shear modulus"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.console_bearing_pad_manual_override}
                  onChange={(e) => handleChange('console_bearing_pad_manual_override')(e.target.checked)}
                  disabled={!formData.console_neoprene_pad_enabled}
                />
              }
              label="Manual Override"
            />
          </Grid>

          {/* Manual override inputs - only shown when checkbox is checked */}
          <Grid item xs={12}>
            <Collapse in={formData.console_neoprene_pad_enabled && formData.console_bearing_pad_manual_override}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Pad Depth (mm)"
                    value={formData.console_bearing_pad_depth * 1000}
                    onChange={(e) => handleChange('console_bearing_pad_depth')((parseFloat(e.target.value) || 0) / 1000)}
                    inputProps={{ min: 50, max: 500, step: 10 }}
                    variant="outlined"
                    helperText="Along beam axis"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Neoprene Layers"
                    value={formData.console_neoprene_layers}
                    onChange={handleChange('console_neoprene_layers')}
                    inputProps={{ min: 1, max: 5, step: 1 }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Layer Thickness (mm)"
                    value={formData.console_neoprene_thickness * 1000}
                    onChange={(e) => handleChange('console_neoprene_thickness')((parseFloat(e.target.value) || 0) / 1000)}
                    inputProps={{ min: 6, max: 20, step: 2 }}
                    variant="outlined"
                    helperText="Single layer ti"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Steel Plate (mm)"
                    value={formData.console_steel_plate_thickness * 1000}
                    onChange={(e) => handleChange('console_steel_plate_thickness')((parseFloat(e.target.value) || 0) / 1000)}
                    inputProps={{ min: 2, max: 6, step: 1 }}
                    variant="outlined"
                    helperText="Reinforcement plate ts"
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>

          {/* Beam Details */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Supported Beam
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Beam Type</InputLabel>
              <Select
                value={formData.console_beam_type}
                onChange={handleChange('console_beam_type')}
                label="Beam Type"
              >
                <MenuItem value="steel">Steel Beam</MenuItem>
                <MenuItem value="concrete">Concrete Beam</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Beam Width (m)"
              value={formData.console_beam_width}
              onChange={handleChange('console_beam_width')}
              inputProps={{ min: 0.1, max: 2, step: 0.01 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Beam Depth (m)"
              value={formData.console_beam_depth}
              onChange={handleChange('console_beam_depth')}
              inputProps={{ min: 0.1, max: 3, step: 0.01 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Angular Change (°)"
              value={formData.console_beam_angular_change}
              onChange={handleChange('console_beam_angular_change')}
              inputProps={{ min: -10, max: 10, step: 0.1 }}
              variant="outlined"
              helperText="Angular change at support"
            />
          </Grid>

          {!formData.console_neoprene_pad_enabled && formData.console_beam_type === 'steel' && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Beam I (m⁴), optional"
                value={formData.console_beam_second_moment_of_area ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  setFormData((prev) => {
                    const next = {
                      ...prev,
                      console_beam_second_moment_of_area:
                        raw === '' ? null : parseFloat(raw),
                    };
                    if (onInputChange) onInputChange(next);
                    return next;
                  });
                }}
                inputProps={{ min: 1e-10, step: 1e-8 }}
                variant="outlined"
                helperText="Second moment of area for direct bearing; leave blank for stiff default"
              />
            </Grid>
          )}

          {/* Dowel Details */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Dowel Connections
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Dowel Material</InputLabel>
              <Select
                value={formData.console_dowel_material}
                onChange={handleChange('console_dowel_material')}
                label="Dowel Material"
              >
                <MenuItem value="steel">Steel</MenuItem>
                <MenuItem value="stainless">Stainless Steel</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Dowel Type</InputLabel>
              <Select
                value={formData.console_dowel_type}
                onChange={handleChange('console_dowel_type')}
                label="Dowel Type"
              >
                <MenuItem value="bolt">Bolt</MenuItem>
                <MenuItem value="rebar">Threaded Rebar</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Dowel Diameter (mm)</InputLabel>
              <Select
                value={(formData.console_dowel_diameter * 1000).toFixed(0)}
                onChange={(e) => handleChange('console_dowel_diameter')(parseFloat(e.target.value) / 1000)}
                label="Dowel Diameter (mm)"
              >
                <MenuItem value="8">8</MenuItem>
                <MenuItem value="10">10</MenuItem>
                <MenuItem value="12">12</MenuItem>
                <MenuItem value="16">16</MenuItem>
                <MenuItem value="20">20</MenuItem>
                <MenuItem value="25">25</MenuItem>
                <MenuItem value="32">32</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Dowel Count"
              value={formData.console_dowel_count}
              onChange={handleChange('console_dowel_count')}
              inputProps={{ min: 1, max: 20, step: 1 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                size="medium"
                disabled={loading}
                sx={{ minWidth: 200 }}
              >
                {loading ? 'Analyzing...' : 'Run Analysis'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default ConsoleInputForm;
